import classnames from 'classnames'
import styles from './Preloader.module.scss'
import useStore from '@/store'
import Logo from '@/components/_svg/Logo'
import RichText from '@/components/RichText/RichText'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import LogoWordmark from '@/components/_svg/LogoWordmark'
import { useCallback, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin'
import useWindowResize from '@/hooks/use-window-resize'
import MovingContainer from '@/components/MovingContainer/MovingContainer'
import useCurrentPage from '@/hooks/use-current-page'
import { deviceInfo } from '@/utils'
gsap.registerPlugin(DrawSVGPlugin)

const LOAD_SPEED = 2.5

const Preloader = ({ className }) => {
  const globalData = useStore(state => state.globalData)
  const allRefs = useRef({
    $centerLogoContainer: null,
    $textLines: null,
    $bottomRightPercentChars: null,
    $bottomLogoContainer: null,
    $percent: null,
    $bg: null,
  })
  const placeholderRef = useRef(0)
  const resizeKey = useWindowResize()
  const setLoaderAnimationComplete = useStore(state => state.setLoaderAnimationComplete)
  const [render, setRender] = useState(true)
  const { isHome } = useCurrentPage()
  const fontsLoaded = useStore(state => state.fontsLoaded)
  const { is404 } = useCurrentPage()
  const isSafariDesktop = deviceInfo?.isSafariDesktop
  const useHomeAnimation = isHome && !isSafariDesktop

  const animateOut = useCallback(() => {
    setTimeout(() => {
      const duration = 0.8

      allRefs.current.$textLines.animateOut({ stagger: 0, duration })

      allRefs.current.$bottomLeftText.animateOut()

      allRefs.current.$bottomRightPercentChars.animateOut()

      if (!useHomeAnimation) {
        gsap.killTweensOf(allRefs.current.$bg)
        gsap.to(allRefs.current.$bg, {
          duration: duration * 2,
          ease: 'Power4.easeInOut',
          scaleY: 0,
          onComplete: () => {
            setRender(false)
          },
        })
      }

      gsap.to([allRefs.current.$centerLogoContainer, allRefs.current.$bottomLogoContainer], {
        autoAlpha: 0,
        duration,
        onComplete: () => {
          if (useHomeAnimation) {
            setRender(false)
          }
        },
      })

      setTimeout(
        () => {
          if (!useHomeAnimation) {
            setLoaderAnimationComplete(true)
          }
        },
        duration * 1000 * 0.3,
      )
    }, 10)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHomeAnimation])

  const animatePercent = useCallback(() => {
    gsap.to(placeholderRef, {
      current: 99,
      duration: LOAD_SPEED * 0.75,
      ease: 'linear',
      onUpdate: () => {
        const value = Math.round(placeholderRef.current)
        let valueFormatted = value.toString()
        if (valueFormatted.length === 1) {
          valueFormatted = ['0', valueFormatted]
        } else {
          valueFormatted = [valueFormatted[0], valueFormatted[1]]
        }

        const $charElements = allRefs.current.$bottomRightPercentChars.getChars()

        $charElements.map(($el, i) => {
          if (!$el) return
          if (i === 0) $el.innerHTML = '('
          if (i === 1) $el.innerHTML = valueFormatted[0]
          if (i === 2) $el.innerHTML = valueFormatted[1]
          if (i === 3) $el.innerHTML = ')'
        })
      },
      onComplete: () => {
        animateOut()

        if (useHomeAnimation) {
          setLoaderAnimationComplete(true)
        }
      },
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLoaderAnimationComplete, animateOut])

  const animatePaths = useCallback(
    $paths => {
      if (!globalData) return
      if (!$paths?.length) return
      const duration = 3
      const ease = 'Power3.easeOut'
      $paths.forEach($el => {
        gsap.fromTo(
          $el,
          {
            drawSVG: 0,
          },
          {
            duration,
            ease,
            stagger: 0.1,
            drawSVG: '100%',
            onComplete: () => {
              gsap.to($el, {
                stroke: 'transparent',
              })
            },
          },
        )
        gsap.to($el, {
          fill: '#f7f4ef',
          duration,
          ease,
          stagger: 0.1,
        })
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resizeKey, globalData],
  )

  const animate = useCallback(() => {
    if (!globalData) return

    const bottomSvgPaths = allRefs?.current?.$bottomLogoContainer?.querySelectorAll('path')
    if (!bottomSvgPaths) return
    const $bottomSvgPaths = Array.from(bottomSvgPaths)
    if ($bottomSvgPaths.length) {
      animatePaths($bottomSvgPaths)
    }

    const middleSvgPaths = allRefs?.current?.$centerLogoContainer?.querySelectorAll('path')
    const $middleSvgPaths = Array.from(middleSvgPaths)
    if ($middleSvgPaths.length) {
      animatePaths($middleSvgPaths)
    }

    setTimeout(() => {
      const $textAnimationRefs = [
        allRefs.current.$textLines,
        allRefs.current.$bottomLeftText,
        allRefs.current.$bottomRightPercentChars,
      ]
      $textAnimationRefs.forEach($ref => {
        $ref.animate()
      })
    }, 20)

    if (useHomeAnimation) {
      setTimeout(() => {
        gsap.killTweensOf(allRefs.current.$bg)
        gsap.set(allRefs.current.$bg, {
          autoAlpha: 0,
        })
      }, 300)
    }

    setTimeout(() => {
      animatePercent()
    }, 300)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalData?.navigation?.preloader?.text, animatePercent, useHomeAnimation])

  useEffect(() => {
    if (!globalData || !render || !fontsLoaded) return
    setTimeout(() => {
      animate()
    }, 150)
  }, [animate, globalData, render, fontsLoaded])

  useEffect(() => {
    if (is404) {
      setLoaderAnimationComplete(true)
      setRender(false)
    }
  }, [is404, setLoaderAnimationComplete])

  if (!globalData || !globalData?.navigation?.preloader?.text || !render) return null
  // if (!globalData || !globalData?.navigation?.preloader?.text || !render) return null

  return (
    <div className={classnames(styles.Preloader, className)}>
      <div
        ref={ref => {
          allRefs.current.$bg = ref
        }}
        className={styles.bg}
      />
      <div className={styles.inner}>
        <div
          className={styles.middle}
          ref={ref => {
            allRefs.current.$centerLogoContainer = ref
          }}
        >
          <MovingContainer strengthMultiplier={0.2}>
            <Logo className={styles.middle__logo} />
            <div className={styles.middle__text}>
              <AnimatedTextLines
                toAnimate="lines"
                querySelector="p"
                animateInConfig={{
                  duration: 1.2,
                  stagger: 0.05,
                }}
                ref={ref => {
                  allRefs.current.$textLines = ref
                }}
              >
                <RichText content={globalData?.navigation?.preloader?.text} />
              </AnimatedTextLines>
            </div>
          </MovingContainer>
        </div>
        <div className={styles.bottomLeft}>
          <AnimatedTextLines
            toAnimate="lines"
            querySelector="span"
            ref={ref => {
              allRefs.current.$bottomLeftText = ref
            }}
            className={styles.bottom__textLines}
          >
            <span>(Loading)</span>
          </AnimatedTextLines>
        </div>
        <div
          className={styles.bottomMiddle}
          ref={ref => {
            allRefs.current.$bottomLogoContainer = ref
          }}
        >
          <LogoWordmark className={styles.bottomMiddle__logo} />
        </div>
        <div className={styles.bottomRight}>
          <AnimatedTextLines
            toAnimate="chars"
            querySelector="span"
            ref={ref => {
              allRefs.current.$bottomRightPercentChars = ref
            }}
            className={styles.bottom__textLines}
            animateInConfig={{
              stagger: 0.05,
            }}
            animateOutConfig={{
              stagger: 0,
            }}
          >
            <span>(00)</span>
          </AnimatedTextLines>
        </div>
      </div>
    </div>
  )
}

Preloader.displayName = 'Preloader'

export default Preloader
